import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, MessageSquare, Send, Check, X, Plus,
  Users, TrendingUp, Clock, Edit2, Trash2,
  ChevronRight, MessageCircle, AlertCircle, Loader2,
  BarChart3, Eye, RefreshCw, CheckCircle, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useBusinessContent } from '../contexts/BusinessContentContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dark Theme Colors
const THEME = {
  bg: '#0f1117',
  cardBg: '#1a2332',
  cardBorder: '#1e2d3d',
  accent: '#00d4aa',
  textPrimary: '#ffffff',
  textSecondary: '#94a3b8',
  warning: '#f59e0b',
  warningBg: '#1a1a00',
  sidebarBg: '#0a1628'
};

export default function WhatsAppIntegration() {
  const { getWhatsAppTemplates, getBusinessTypeName } = useBusinessContent();
  const businessWhatsAppTemplates = getWhatsAppTemplates();
  
  const DEFAULT_TEMPLATES = React.useMemo(() => {
    return [
      {
        id: "welcome",
        name: "Welcome",
        template: businessWhatsAppTemplates?.greeting || "Hi [name]! Welcome to [company]. We received your enquiry and will contact you shortly.\nReply STOP to unsubscribe.",
        category: "greeting"
      },
      {
        id: "follow_up",
        name: "Follow Up",
        template: businessWhatsAppTemplates?.followup || "Hi [name]! Following up on your enquiry about [product]. Are you still interested?\nReply YES to know more.",
        category: "follow_up"
      },
      {
        id: "document_share",
        name: "Document Share",
        template: "Hi [name]! Please find the document you requested:\n[document_link]\nLet us know if you have questions.",
        category: "document"
      },
      {
        id: "meeting_reminder",
        name: "Meeting Reminder",
        template: "Hi [name]! Reminder about your meeting tomorrow at [time].\nReply CONFIRM to confirm or RESCHEDULE to change time.",
        category: "reminder"
      },
      {
        id: "special_offer",
        name: "Special Offer",
        template: businessWhatsAppTemplates?.offer || "Hi [name]! Special offer on [product_name] - [discount]% off!\nVisit now: [link]\nOffer valid till [date].",
        category: "promotion"
      },
      {
        id: "renewal",
        name: "Renewal Reminder",
        template: "Hi [name]! Your service is due for renewal on [date].\nContact us to renew: [phone] or reply here.",
        category: "reminder"
      }
    ];
  }, [businessWhatsAppTemplates]);

  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState({ default_templates: DEFAULT_TEMPLATES, custom_templates: [] });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [showTestMessage, setShowTestMessage] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [sendingTest, setSendingTest] = useState(false);
  
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('This is a test message from AILeadMantra WhatsApp integration.');
  
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateText, setNewTemplateText] = useState('');

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, statsRes, templatesRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/whatsapp/config`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/whatsapp/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/whatsapp/templates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/whatsapp/history`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (configRes.ok) {
        const data = await configRes.json();
        setConfig(data);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates({
          default_templates: data.default_templates || DEFAULT_TEMPLATES,
          custom_templates: data.custom_templates || []
        });
      }
      if (historyRes.ok) {
        const data = await historyRes.json();
        setHistory(data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setSendingTest(true);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/send-test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone: testPhone,
          message: testMessage
        })
      });

      if (res.ok) {
        toast.success('Test message sent!');
        setShowTestMessage(false);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!newTemplateName || !newTemplateText) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/whatsapp/templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newTemplateName,
          template: newTemplateText
        })
      });

      if (res.ok) {
        toast.success('Template saved!');
        setShowTemplateForm(false);
        setNewTemplateName('');
        setNewTemplateText('');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;

    try {
      const res = await fetch(`${API_URL}/api/whatsapp/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Template deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'bg-[#00d4aa]/20 text-[#00d4aa]';
      case 'read': return 'bg-blue-500/20 text-blue-400';
      case 'sent': return 'bg-yellow-500/20 text-yellow-400';
      case 'failed': return 'bg-red-500/20 text-red-400';
      case 'received': return 'bg-purple-500/20 text-purple-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" style={{ backgroundColor: THEME.bg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: THEME.accent }} />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen p-6 space-y-6" 
      style={{ backgroundColor: THEME.bg }}
      data-testid="whatsapp-integration"
    >
      {/* Header Card */}
      <div 
        className="rounded-xl p-5 flex items-center justify-between"
        style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
      >
        <div className="flex items-center gap-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${THEME.accent}20` }}
          >
            <MessageCircle className="w-6 h-6" style={{ color: THEME.accent }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: THEME.textPrimary }}>
              {getBusinessTypeName()} WhatsApp
            </h1>
            <p style={{ color: THEME.textSecondary }}>Send automated WhatsApp messages to your leads</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Badge 
            className="px-3 py-1.5"
            style={{ backgroundColor: `${THEME.accent}20`, color: THEME.accent, border: `1px solid ${THEME.accent}30` }}
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            Connected
          </Badge>
          <Button
            onClick={() => setShowTestMessage(true)}
            className="font-medium"
            style={{ backgroundColor: THEME.accent, color: '#000000' }}
          >
            <Send className="w-4 h-4 mr-2" />
            Test Message
          </Button>
        </div>
      </div>

      {/* Connection Status Card */}
      <div 
        className="rounded-xl p-6"
        style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold mb-3" style={{ color: THEME.textPrimary }}>
              WhatsApp Integration
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" style={{ color: THEME.accent }} />
                <span style={{ color: THEME.textSecondary }}>
                  Status: <span style={{ color: THEME.accent }} className="font-medium">Connected</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" style={{ color: THEME.accent }} />
                <span style={{ color: THEME.textSecondary }}>
                  Number: <span style={{ color: THEME.textPrimary }} className="font-medium">+1 870 454 3901</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4" style={{ color: THEME.accent }} />
                <span style={{ color: THEME.textSecondary }}>
                  Mode: <span style={{ color: THEME.textPrimary }} className="font-medium">Business Messaging</span>
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm mb-1" style={{ color: THEME.textSecondary }}>Messages sent today</p>
            <p className="text-3xl font-bold" style={{ color: THEME.textPrimary }}>{stats?.messages_today || 0}</p>
            <p className="text-sm mt-2" style={{ color: THEME.textSecondary }}>This month: {stats?.messages_this_month || 0}</p>
          </div>
        </div>
      </div>

      {/* WhatsApp Mode Banner */}
      <div 
        className="rounded-xl p-5"
        style={{ 
          backgroundColor: `${THEME.accent}10`, 
          border: `1px solid ${THEME.accent}30`
        }}
      >
        <div className="flex items-start gap-4">
          <div 
            className="p-3 rounded-xl"
            style={{ backgroundColor: `${THEME.accent}20` }}
          >
            <MessageCircle className="w-6 h-6" style={{ color: THEME.accent }} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold mb-1" style={{ color: THEME.textPrimary }}>
              WhatsApp Mode: Click-to-Chat Links
            </h4>
            <p className="text-sm mb-3" style={{ color: THEME.textSecondary }}>
              Leads receive WhatsApp links in agent notifications. Agent clicks to message directly from lead timeline.
              Each link opens WhatsApp with a pre-filled message!
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Badge style={{ backgroundColor: `${THEME.accent}20`, color: THEME.accent }}>
                <CheckCircle className="w-3 h-3 mr-1" />
                Active
              </Badge>
              <span className="text-xs" style={{ color: THEME.textSecondary }}>
                Want full automation? Setup WhatsApp Business API
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div 
          className="rounded-xl p-5"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.accent}20` }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: THEME.textSecondary }}>Messages Today</p>
              <p className="text-3xl font-bold mt-1" style={{ color: THEME.textPrimary }}>
                {stats?.messages_today || 0}
              </p>
            </div>
            <div className="p-3 rounded-xl" style={{ backgroundColor: `${THEME.accent}20` }}>
              <Send className="w-6 h-6" style={{ color: THEME.accent }} />
            </div>
          </div>
        </div>

        <div 
          className="rounded-xl p-5"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: THEME.textSecondary }}>This Month</p>
              <p className="text-3xl font-bold mt-1" style={{ color: THEME.textPrimary }}>
                {stats?.messages_this_month || 0}
              </p>
            </div>
            <div className="p-3 rounded-xl" style={{ backgroundColor: '#6366f120' }}>
              <BarChart3 className="w-6 h-6 text-indigo-400" />
            </div>
          </div>
        </div>

        <div 
          className="rounded-xl p-5"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: THEME.textSecondary }}>Delivery Rate</p>
              <p className="text-3xl font-bold mt-1" style={{ color: THEME.accent }}>
                {stats?.delivery_rate || 98}%
              </p>
            </div>
            <div className="p-3 rounded-xl" style={{ backgroundColor: '#8b5cf620' }}>
              <CheckCircle className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </div>

        <div 
          className="rounded-xl p-5"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: THEME.textSecondary }}>Reply Rate</p>
              <p className="text-3xl font-bold mt-1" style={{ color: THEME.textSecondary }}>
                {stats?.reply_rate || 0}%
              </p>
            </div>
            <div className="p-3 rounded-xl" style={{ backgroundColor: '#f9731620' }}>
              <MessageSquare className="w-6 h-6 text-orange-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {['overview', 'templates', 'history'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="px-4 py-2 rounded-lg font-medium capitalize transition-all"
            style={{
              backgroundColor: activeTab === tab ? `${THEME.accent}20` : THEME.cardBg,
              color: activeTab === tab ? THEME.accent : THEME.textSecondary,
              borderBottom: activeTab === tab ? `2px solid ${THEME.accent}` : 'none'
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'templates' && (
        <div 
          className="rounded-xl p-6"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: THEME.textPrimary }}>
              Message Templates
            </h3>
            <Button
              onClick={() => setShowTemplateForm(true)}
              style={{ backgroundColor: THEME.accent, color: '#000000' }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Template
            </Button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[...templates.default_templates, ...templates.custom_templates].map((template, idx) => (
              <div 
                key={template.id || idx}
                className="rounded-lg p-4"
                style={{ backgroundColor: THEME.bg, border: `1px solid ${THEME.cardBorder}` }}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium" style={{ color: THEME.textPrimary }}>{template.name}</h4>
                  {template.category && (
                    <Badge style={{ backgroundColor: `${THEME.accent}20`, color: THEME.accent }}>
                      {template.category}
                    </Badge>
                  )}
                </div>
                <p className="text-sm" style={{ color: THEME.textSecondary }}>{template.template}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div 
          className="rounded-xl p-6"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: THEME.textPrimary }}>
            Message History
          </h3>
          {history.length === 0 ? (
            <p style={{ color: THEME.textSecondary }}>No messages sent yet</p>
          ) : (
            <div className="space-y-3">
              {history.map((msg, idx) => (
                <div 
                  key={idx}
                  className="rounded-lg p-4 flex items-center justify-between"
                  style={{ backgroundColor: THEME.bg, border: `1px solid ${THEME.cardBorder}` }}
                >
                  <div>
                    <p style={{ color: THEME.textPrimary }}>{msg.to_number}</p>
                    <p className="text-sm truncate max-w-md" style={{ color: THEME.textSecondary }}>
                      {msg.message}
                    </p>
                  </div>
                  <Badge className={getStatusColor(msg.status)}>{msg.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'overview' && (
        <div 
          className="rounded-xl p-6"
          style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: THEME.textPrimary }}>
            Quick Actions
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <button 
              onClick={() => setShowTestMessage(true)}
              className="p-4 rounded-lg text-left transition-all hover:scale-[1.02]"
              style={{ backgroundColor: THEME.bg, border: `1px solid ${THEME.cardBorder}` }}
            >
              <Send className="w-8 h-8 mb-2" style={{ color: THEME.accent }} />
              <h4 className="font-medium" style={{ color: THEME.textPrimary }}>Send Test Message</h4>
              <p className="text-sm" style={{ color: THEME.textSecondary }}>Try the integration</p>
            </button>
            <button 
              onClick={() => setActiveTab('templates')}
              className="p-4 rounded-lg text-left transition-all hover:scale-[1.02]"
              style={{ backgroundColor: THEME.bg, border: `1px solid ${THEME.cardBorder}` }}
            >
              <MessageSquare className="w-8 h-8 mb-2" style={{ color: THEME.accent }} />
              <h4 className="font-medium" style={{ color: THEME.textPrimary }}>View Templates</h4>
              <p className="text-sm" style={{ color: THEME.textSecondary }}>{templates.default_templates.length + templates.custom_templates.length} templates</p>
            </button>
            <button 
              onClick={() => setActiveTab('history')}
              className="p-4 rounded-lg text-left transition-all hover:scale-[1.02]"
              style={{ backgroundColor: THEME.bg, border: `1px solid ${THEME.cardBorder}` }}
            >
              <Clock className="w-8 h-8 mb-2" style={{ color: THEME.accent }} />
              <h4 className="font-medium" style={{ color: THEME.textPrimary }}>Message History</h4>
              <p className="text-sm" style={{ color: THEME.textSecondary }}>{history.length} messages</p>
            </button>
          </div>
        </div>
      )}

      {/* Test Message Modal */}
      <AnimatePresence>
        {showTestMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
            onClick={() => setShowTestMessage(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-xl p-6"
              style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
            >
              <h3 className="text-xl font-bold mb-4" style={{ color: THEME.textPrimary }}>
                Send Test Message
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: THEME.textSecondary }}>
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+91..."
                    className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2"
                    style={{ 
                      backgroundColor: THEME.bg, 
                      border: `1px solid ${THEME.cardBorder}`,
                      color: THEME.textPrimary,
                      focusRingColor: THEME.accent
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: THEME.textSecondary }}>
                    Message
                  </label>
                  <textarea
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2"
                    style={{ 
                      backgroundColor: THEME.bg, 
                      border: `1px solid ${THEME.cardBorder}`,
                      color: THEME.textPrimary
                    }}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowTestMessage(false)}
                    variant="outline"
                    className="flex-1"
                    style={{ borderColor: THEME.cardBorder, color: THEME.textSecondary }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSendTestMessage}
                    disabled={sendingTest}
                    className="flex-1"
                    style={{ backgroundColor: THEME.accent, color: '#000000' }}
                  >
                    {sendingTest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Send
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Template Modal */}
      <AnimatePresence>
        {showTemplateForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
            onClick={() => setShowTemplateForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-xl p-6"
              style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.cardBorder}` }}
            >
              <h3 className="text-xl font-bold mb-4" style={{ color: THEME.textPrimary }}>
                Add Template
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: THEME.textSecondary }}>
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    placeholder="e.g., Welcome Message"
                    className="w-full px-4 py-3 rounded-lg focus:outline-none"
                    style={{ 
                      backgroundColor: THEME.bg, 
                      border: `1px solid ${THEME.cardBorder}`,
                      color: THEME.textPrimary
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: THEME.textSecondary }}>
                    Message Template
                  </label>
                  <textarea
                    value={newTemplateText}
                    onChange={(e) => setNewTemplateText(e.target.value)}
                    rows={4}
                    placeholder="Hi [name], ..."
                    className="w-full px-4 py-3 rounded-lg focus:outline-none"
                    style={{ 
                      backgroundColor: THEME.bg, 
                      border: `1px solid ${THEME.cardBorder}`,
                      color: THEME.textPrimary
                    }}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowTemplateForm(false)}
                    variant="outline"
                    className="flex-1"
                    style={{ borderColor: THEME.cardBorder, color: THEME.textSecondary }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveTemplate}
                    className="flex-1"
                    style={{ backgroundColor: THEME.accent, color: '#000000' }}
                  >
                    Save Template
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
